import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { FaTimes, FaExclamationTriangle, FaCheck, FaUndo, FaFilter, FaCube, FaPlus, FaTrashAlt } from 'react-icons/fa';
import api from '../../config/axios';
import { logger } from '../../utils/logger';

/**
 * Modale che mostra le differenze tra attributi vecchi e nuovi durante il censimento.
 * L'utente può scegliere quali valori vecchi preservare tramite checkbox.
 * Le differenze sono raggruppate per tipo oggetto (Item / ItemFiltro).
 * 
 * Props:
 * - isOpen: boolean
 * - differences: array di { ATT_Nome, ATT_Descrizione, objectType, objectName, isNew?, isRemoved?, changedFields: [{ field, oldValue, newValue }] }
 * - tableName: string
 * - onConfirm: (preserveMap) => void  — preserveMap = { "main::ATT_Nome": { field: oldValue, ... }, "filter::ATT_Nome": { ... } }
 * - onCancel: () => void
 * - themeColor: string (es. 'indigo' o 'emerald')
 */
const CensimentoDifferencesModal = ({ isOpen, differences = [], tableName, onConfirm, onCancel, themeColor = 'indigo' }) => {
  // Chiave composita per distinguere attributi con stesso nome in oggetti diversi
  const getDiffKey = (diff) => {
    const type = diff.objectType || 'main';
    return `${type}::${diff.ATT_Nome}`;
  };

  // Stato: per ogni diffKey, per ogni field, se accettare il nuovo valore dal censimento (true) o preservare il vecchio (false)
  const [preserveSelections, setPreserveSelections] = useState({});

  // Lookup maps per risolvere FK → descrizione
  const [lookupMaps, setLookupMaps] = useState({});

  // Configurazione lookup: field → { table, idField, labelField }
  const LOOKUP_FIELDS = useMemo(() => ({
    ATT_Id_TipiValidazioni: { table: 'SKE_TipiValidazioni', idField: 'TIV_Id_TipiValidazioni', labelField: 'TIV_Descrizione' },
    ATT_TipiCampi: { table: 'SKE_TipiCampi', idField: 'TIP_Id_TipiCampi', labelField: 'TIP_Descrizione' },
    ATT_Super_Oggetti: { table: 'SKE_Oggetti', idField: 'OGG_Id_Oggetti', labelField: 'OGG_Nome' },
  }), []);

  // Inizializza le selezioni quando le differenze cambiano
  useEffect(() => {
    if (differences.length > 0) {
      const initial = {};
      for (const diff of differences) {
        const key = getDiffKey(diff);
        initial[key] = {};
        if (diff.isRemoved) {
          // Pre-seleziona ripristino solo per attributi con "Id" nel nome (I maiuscola, d minuscola)
          initial[key].__restoreRemoved = /Id/.test(diff.ATT_Nome);
        } else if (diff.isNew) {
          // Default: accetta il nuovo attributo
          initial[key].__acceptNew = true;
        } else {
          for (const cf of diff.changedFields) {
            initial[key][cf.field] = true; // default: accetta il nuovo valore dal censimento
          }
        }
      }
      setPreserveSelections(initial);
    }
  }, [differences]);

  // Carica lookup per i campi FK presenti nelle differenze
  useEffect(() => {
    if (!isOpen || differences.length === 0) return;

    // Trova quali campi FK sono effettivamente presenti nelle differenze
    const fkFieldsPresent = new Set();
    for (const diff of differences) {
      for (const cf of diff.changedFields) {
        if (LOOKUP_FIELDS[cf.field]) {
          fkFieldsPresent.add(cf.field);
        }
      }
    }

    if (fkFieldsPresent.size === 0) return;

    const fetchLookups = async () => {
      const maps = {};
      for (const fieldName of fkFieldsPresent) {
        const config = LOOKUP_FIELDS[fieldName];
        try {
          const response = await api.get(`/crud/${config.table}`, { params: { limit: 1000 } });
          const records = response.data?.records || [];
          const map = {};
          for (const rec of records) {
            map[rec[config.idField]] = rec[config.labelField] || `ID ${rec[config.idField]}`;
          }
          maps[fieldName] = map;
        } catch (err) {
          logger.error(`[CensimentoDifferencesModal] Errore fetch lookup ${config.table}:`, err);
        }
      }
      setLookupMaps(maps);
    };

    fetchLookups();
  }, [isOpen, differences, LOOKUP_FIELDS]);

  const toggleField = (diffKey, field) => {
    setPreserveSelections(prev => ({
      ...prev,
      [diffKey]: {
        ...prev[diffKey],
        [field]: !prev[diffKey]?.[field]
      }
    }));
  };

  const toggleAllForAttribute = (diffKey, value) => {
    setPreserveSelections(prev => {
      const diff = differences.find(d => getDiffKey(d) === diffKey);
      if (!diff) return prev;
      if (diff.isRemoved) {
        // Per rimossi: logica inversa — "sovrascrivi" (!value) = non ripristinare, "preserva" (!value=true) = ripristina
        return { ...prev, [diffKey]: { __restoreRemoved: !value } };
      }
      if (diff.isNew) {
        return { ...prev, [diffKey]: { __acceptNew: value } };
      }
      const fields = {};
      for (const cf of diff.changedFields) {
        fields[cf.field] = value;
      }
      return { ...prev, [diffKey]: fields };
    });
  };

  // Toggle tutti gli attributi di un intero objectType (main o filter)
  const toggleAllForObjectType = (objectType, value) => {
    setPreserveSelections(prev => {
      const updated = { ...prev };
      for (const diff of differences) {
        if ((diff.objectType || 'main') !== objectType) continue;
        const key = getDiffKey(diff);
        if (diff.isRemoved) {
          // Per rimossi: logica inversa — "sovrascrivi" (!value) = non ripristinare
          updated[key] = { __restoreRemoved: !value };
        } else if (diff.isNew) {
          updated[key] = { __acceptNew: value };
        } else {
          const fields = {};
          for (const cf of diff.changedFields) {
            fields[cf.field] = value;
          }
          updated[key] = fields;
        }
      }
      return updated;
    });
  };

  // Raggruppa differenze per objectType
  const groupedDifferences = useMemo(() => {
    const groups = {};
    for (const diff of differences) {
      const type = diff.objectType || 'main';
      if (!groups[type]) groups[type] = [];
      groups[type].push(diff);
    }
    return groups;
  }, [differences]);

  const objectTypeLabels = {
    main: { label: 'Item (Oggetto Principale)', icon: FaCube, color: 'blue' },
    filter: { label: 'Item Filtro', icon: FaFilter, color: 'purple' },
  };

  // Classi esplicite per evitare problemi con Tailwind JIT e classi dinamiche
  const objectTypeStyles = {
    blue: {
      header: 'bg-blue-50 dark:bg-blue-950',
      icon: 'text-blue-600 dark:text-blue-400',
      title: 'text-blue-800 dark:text-blue-200',
      subtitle: 'text-blue-600 dark:text-blue-400',
      badge: 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300',
    },
    purple: {
      header: 'bg-purple-50 dark:bg-purple-900',
      icon: 'text-purple-600 dark:text-purple-300',
      title: 'text-purple-800 dark:text-purple-200',
      subtitle: 'text-purple-600 dark:text-purple-300',
      badge: 'bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300',
    },
    gray: {
      header: 'bg-gray-50 dark:bg-gray-800',
      icon: 'text-gray-600 dark:text-gray-400',
      title: 'text-gray-800 dark:text-gray-200',
      subtitle: 'text-gray-600 dark:text-gray-400',
      badge: 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300',
    },
  };

  const totalChanges = useMemo(() => differences.reduce((sum, d) => sum + d.changedFields.length, 0), [differences]);
  const newCount = useMemo(() => differences.filter(d => d.isNew).length, [differences]);
  const removedCount = useMemo(() => differences.filter(d => d.isRemoved).length, [differences]);
  const modifiedCount = useMemo(() => differences.filter(d => !d.isNew && !d.isRemoved).length, [differences]);
  
  const preservedCount = useMemo(() => {
    let count = 0;
    for (const attrFields of Object.values(preserveSelections)) {
      for (const [key, val] of Object.entries(attrFields)) {
        if (!val && key !== '__restoreRemoved' && key !== '__acceptNew') count++;
      }
    }
    return count;
  }, [preserveSelections]);

  const restoredCount = useMemo(() => {
    let count = 0;
    for (const attrFields of Object.values(preserveSelections)) {
      if (attrFields.__restoreRemoved) count++;
    }
    return count;
  }, [preserveSelections]);

  const rejectedNewCount = useMemo(() => {
    let count = 0;
    for (const [key, attrFields] of Object.entries(preserveSelections)) {
      if ('__acceptNew' in attrFields && !attrFields.__acceptNew) count++;
    }
    return count;
  }, [preserveSelections]);

  const handleConfirm = () => {
    // Costruisci preserveMap con chiavi composite "objectType::ATT_Nome"
    const preserveMap = {};
    for (const diff of differences) {
      const key = getDiffKey(diff);
      const selectedFields = preserveSelections[key] || {};

      // Gestione attributi rimossi da ripristinare
      if (diff.isRemoved) {
        if (selectedFields.__restoreRemoved) {
          preserveMap[key] = { __restoreRemoved: true };
        }
        continue;
      }

      // Gestione attributi nuovi da rifiutare
      if (diff.isNew) {
        if (!selectedFields.__acceptNew) {
          preserveMap[key] = { __rejectNew: true };
        }
        continue;
      }

      // Gestione attributi con differenze campi — i campi NON selezionati (false) vanno preservati
      const fieldsToPreserve = {};
      let hasAny = false;
      for (const cf of diff.changedFields) {
        if (!selectedFields[cf.field]) {
          fieldsToPreserve[cf.field] = cf.oldValue;
          hasAny = true;
        }
      }
      if (hasAny) {
        preserveMap[key] = fieldsToPreserve;
      }
    }
    onConfirm(preserveMap);
  };

  // Nomi human-friendly per i campi ATT_
  const fieldLabels = {
    ATT_Sequenza: 'Sequenza',
    ATT_Nome: 'Nome',
    ATT_Descrizione: 'Descrizione',
    ATT_TipiCampi: 'Tipo Campo',
    ATT_Lunghezza: 'Lunghezza',
    ATT_Super_Oggetti: 'Super Oggetto',
    ATT_NomeAttributoChiaveEsterna: 'Chiave Esterna',
    ATT_NomeAttributoChiaveEsterno: 'Chiave Esterna',
    ATT_Lista: 'Lista',
    ATT_Like: 'Ricerca Like',
    ATT_AccettaZero: 'Accetta Zero',
    ATT_Manuale: 'Manuale',
    ATT_Default: 'Default',
    ATT_Id_TipiValidazioni: 'Tipo Validazione',
  };

  // Formatta il valore, risolvendo FK tramite lookup se disponibile
  const formatValue = useCallback((value, fieldName) => {
    if (value === null || value === undefined) return '(vuoto)';
    // Se è un campo FK con lookup disponibile, mostra descrizione + ID
    const map = lookupMaps[fieldName];
    if (map && value !== 0 && value !== '0') {
      const label = map[value];
      if (label) return `${label} (ID: ${value})`;
    }
    if (value === 0 || value === '0') return '0';
    return String(value);
  }, [lookupMaps]);

  if (!isOpen || differences.length === 0) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-2xl max-w-7xl w-full max-h-[85vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className={`flex justify-between items-center px-6 py-4 bg-amber-100 dark:bg-amber-900 border-b border-gray-300 dark:border-gray-700`}>
          <div className="flex items-center gap-3">
            <FaExclamationTriangle className="text-amber-600 dark:text-amber-400 h-5 w-5" />
            <div>
              <h3 className="text-lg font-semibold text-amber-800 dark:text-amber-100">
                Differenze rilevate — {tableName}
              </h3>
              <p className="text-sm text-amber-600 dark:text-amber-300">
                {modifiedCount > 0 && <span>{modifiedCount} modificati ({totalChanges} campi)</span>}
                {newCount > 0 && <span>{modifiedCount > 0 ? ' · ' : ''}{newCount} nuovi</span>}
                {removedCount > 0 && <span>{(modifiedCount > 0 || newCount > 0) ? ' · ' : ''}{removedCount} rimossi</span>}
              </p>
            </div>
          </div>
          <button onClick={onCancel} className="text-amber-600 hover:text-amber-800 dark:text-amber-400 dark:hover:text-amber-200">
            <FaTimes className="h-5 w-5" />
          </button>
        </div>

        {/* Info bar */}
        <div className="px-6 py-3 bg-blue-50 dark:bg-blue-950 border-b border-gray-200 dark:border-gray-700 text-sm">
          <p className="text-blue-800 dark:text-blue-200">
            <strong>Checkbox selezionata</strong> = accetta il nuovo valore dal censimento. <br />
            <strong>Checkbox deselezionata</strong> = mantieni il valore vecchio (attuale in DB).
          </p>
          
        </div>

        {/* Content — raggruppato per objectType, su due colonne se ci sono entrambi */}
        <div className="p-6 overflow-y-auto flex-grow">
          <div className={`grid gap-4 ${Object.keys(groupedDifferences).length > 1 ? 'grid-cols-2' : 'grid-cols-1'}`}>
            {Object.entries(groupedDifferences).map(([objectType, diffs]) => {
              const typeInfo = objectTypeLabels[objectType] || { label: objectType, icon: FaCube, color: 'gray' };
              const TypeIcon = typeInfo.icon;
              const styles = objectTypeStyles[typeInfo.color] || objectTypeStyles.gray;
              const sectionDiffCount = diffs.reduce((sum, d) => sum + d.changedFields.length, 0);
              
              return (
                <div key={objectType} className="border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden flex flex-col">
                  {/* Section header per objectType */}
                  <div className={`flex-shrink-0 flex items-center justify-between px-4 py-3 ${styles.header} border-b border-gray-200 dark:border-gray-700`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <TypeIcon className={`${styles.icon} h-4 w-4`} />
                      <span className={`font-semibold ${styles.title} text-sm`}>
                        {typeInfo.label}
                      </span>
                      {diffs[0]?.objectName && (
                        <span className={`text-xs ${styles.subtitle}`}>
                          — {diffs[0].objectName}
                        </span>
                      )}
                      <span className={`text-xs px-2 py-0.5 rounded-full ${styles.badge}`}>
                        {diffs.length} attr, {sectionDiffCount} mod
                      </span>
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <button
                        onClick={() => toggleAllForObjectType(objectType, true)}
                        className="text-xs px-2 py-1 rounded bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-800"
                        title={`Sovrascrivi tutti i valori di ${typeInfo.label}`}
                      >
                        <FaCheck className="inline mr-1 h-3 w-3" />Sovrascrivi
                      </button>
                      <button
                        onClick={() => toggleAllForObjectType(objectType, false)}
                        className="text-xs px-2 py-1 rounded bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 hover:bg-green-200 dark:hover:bg-green-800"
                        title={`Preserva tutti i vecchi valori di ${typeInfo.label}`}
                      >
                        <FaUndo className="inline mr-1 h-3 w-3" />Preserva
                      </button>
                    </div>
                  </div>

                  {/* Attributi di questa sezione — scroll indipendente */}
                  <div className="overflow-y-auto flex-grow divide-y divide-gray-300 dark:divide-gray-600">
                    {diffs.map((diff) => {
                      const diffKey = getDiffKey(diff);

                      // Attributo NUOVO (presente solo nel nuovo censimento) — con opzione di rifiutare
                      if (diff.isNew) {
                        const isAccepted = preserveSelections[diffKey]?.__acceptNew ?? true;
                        const toggleNew = () => setPreserveSelections(prev => ({
                          ...prev,
                          [diffKey]: { __acceptNew: !isAccepted }
                        }));
                        return (
                          <div key={diffKey} className={`px-4 py-3 border-l-4 transition-colors cursor-pointer select-none ${isAccepted ? 'bg-emerald-100 dark:bg-emerald-900/60 border-l-emerald-600' : 'bg-gray-100 dark:bg-gray-800 border-l-gray-300 dark:border-l-gray-600'}`} onClick={toggleNew}>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <input
                                  type="checkbox"
                                  checked={isAccepted}
                                  onChange={toggleNew}
                                  onClick={(e) => e.stopPropagation()}
                                  className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 cursor-pointer accent-emerald-600"
                                  title={isAccepted ? 'Deseleziona per rifiutare questo nuovo attributo' : 'Seleziona per accettare questo nuovo attributo'}
                                />
                                <FaPlus className={`h-3 w-3 ${isAccepted ? 'text-emerald-700 dark:text-emerald-300' : 'text-gray-400 dark:text-gray-500'}`} />
                                <span className={`font-semibold ${isAccepted ? 'text-emerald-900 dark:text-emerald-100' : 'text-gray-400 dark:text-gray-500 line-through'}`}>{diff.ATT_Nome}</span>
                                <span className={`text-xs px-2 py-0.5 rounded font-bold border ${isAccepted ? 'bg-emerald-200 dark:bg-emerald-800 text-emerald-800 dark:text-emerald-200 border-emerald-400 dark:border-emerald-600' : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 border-gray-300 dark:border-gray-600'}`}>
                                  {isAccepted ? 'NUOVO' : 'RIFIUTATO'}
                                </span>
                                {diff.ATT_Descrizione && (
                                  <span className={`text-xs ${isAccepted ? 'text-emerald-700 dark:text-emerald-300' : 'text-gray-400 dark:text-gray-500'}`}>— {diff.ATT_Descrizione}</span>
                                )}
                              </div>
                              <span className={`text-xs font-medium ${isAccepted ? 'text-emerald-700 dark:text-emerald-300' : 'text-gray-400 dark:text-gray-500'}`}>
                                {isAccepted ? 'Verrà inserito' : 'Non verrà inserito'}
                              </span>
                            </div>
                          </div>
                        );
                      }

                      // Attributo RIMOSSO (presente solo nel vecchio censimento) — con opzione di preservare
                      if (diff.isRemoved) {
                        const isPreserved = preserveSelections[diffKey]?.__restoreRemoved ?? false;
                        const toggleRemoved = () => setPreserveSelections(prev => ({
                          ...prev,
                          [diffKey]: { __restoreRemoved: !isPreserved }
                        }));
                        return (
                          <div key={diffKey} className={`px-4 py-3 border-l-4 transition-colors cursor-pointer select-none ${isPreserved ? 'bg-amber-100 dark:bg-amber-900/60 border-l-amber-600' : 'bg-red-100 dark:bg-red-900/40 border-l-red-600'}`} onClick={toggleRemoved}>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <input
                                  type="checkbox"
                                  checked={isPreserved}
                                  onChange={toggleRemoved}
                                  onClick={(e) => e.stopPropagation()}
                                  className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 cursor-pointer accent-amber-600"
                                  title={isPreserved ? 'Deseleziona per rimuovere questo attributo' : 'Seleziona per ripristinare questo attributo'}
                                />
                                <FaTrashAlt className={`h-3 w-3 ${isPreserved ? 'text-amber-700 dark:text-amber-300' : 'text-red-700 dark:text-red-300'}`} />
                                <span className={`font-semibold ${isPreserved ? 'text-amber-900 dark:text-amber-100' : 'text-red-900 dark:text-red-100 line-through'}`}>{diff.ATT_Nome}</span>
                                <span className={`text-xs px-2 py-0.5 rounded font-bold border ${isPreserved ? 'bg-amber-200 dark:bg-amber-800 text-amber-800 dark:text-amber-200 border-amber-400 dark:border-amber-600' : 'bg-red-200 dark:bg-red-800 text-red-800 dark:text-red-200 border-red-400 dark:border-red-600'}`}>
                                  {isPreserved ? 'RIPRISTINA' : 'RIMOSSO'}
                                </span>
                                {diff.ATT_Descrizione && (
                                  <span className={`text-xs ${isPreserved ? 'text-amber-700 dark:text-amber-300' : 'text-red-700 dark:text-red-300'}`}>— {diff.ATT_Descrizione}</span>
                                )}
                              </div>
                              <span className={`text-xs font-medium ${isPreserved ? 'text-amber-700 dark:text-amber-300' : 'text-red-700 dark:text-red-300'}`}>
                                {isPreserved ? 'Verrà ripristinato' : 'Non presente nel censimento'}
                              </span>
                            </div>
                          </div>
                        );
                      }

                      // Attributo con differenze (caso standard)
                      return (
                        <div key={diffKey} className="border-l-4 border-l-blue-500">
                          {/* Attribute header */}
                          <div className="flex items-center justify-between px-4 py-2.5 bg-gray-200 dark:bg-gray-700 border-b border-gray-300 dark:border-gray-600">
                            <div>
                              <span className="font-medium text-gray-900 dark:text-gray-100">{diff.ATT_Nome}</span>
                              {diff.ATT_Descrizione && (
                                <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">— {diff.ATT_Descrizione}</span>
                              )}
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={() => toggleAllForAttribute(diffKey, true)}
                                className="text-xs px-2 py-1 rounded bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-800"
                                title="Accetta tutti i nuovi valori"
                              >
                                <FaCheck className="inline mr-1 h-3 w-3" />Sovrascrivi tutti
                              </button>
                              <button
                                onClick={() => toggleAllForAttribute(diffKey, false)}
                                className="text-xs px-2 py-1 rounded bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 hover:bg-green-200 dark:hover:bg-green-800"
                                title="Preserva tutti i vecchi valori"
                              >
                                <FaUndo className="inline mr-1 h-3 w-3" />Preserva tutti
                              </button>
                            </div>
                          </div>
                          {/* Changed fields table */}
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 text-xs border-b border-gray-300 dark:border-gray-600">
                                <th className="px-4 py-2 text-left w-10">Accetta</th>
                                <th className="px-4 py-2 text-left">Campo</th>
                                <th className="px-4 py-2 text-left">Nuovo Valore (Censimento)</th>
                                <th className="px-4 py-2 text-left">Valore Attuale (DB)</th>
                              </tr>
                            </thead>
                            <tbody>
                              {diff.changedFields.map((cf) => {
                                const isAccepted = preserveSelections[diffKey]?.[cf.field] ?? true;
                                return (
                                  <tr key={cf.field} className={`border-t border-gray-300 dark:border-gray-600 cursor-pointer select-none ${isAccepted ? 'bg-green-100 dark:bg-green-900/50' : 'bg-white dark:bg-gray-900'}`} onClick={() => toggleField(diffKey, cf.field)}>
                                    <td className="px-4 py-2 text-center">
                                      <input
                                        type="checkbox"
                                        checked={isAccepted}
                                        onChange={() => toggleField(diffKey, cf.field)}
                                        onClick={(e) => e.stopPropagation()}
                                        className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 cursor-pointer"
                                      />
                                    </td>
                                    <td className="px-4 py-2 font-medium text-gray-700 dark:text-gray-300">
                                      {fieldLabels[cf.field] || cf.field}
                                    </td>
                                    <td className={`px-4 py-2 ${isAccepted ? 'font-semibold text-blue-700 dark:text-blue-400' : 'text-gray-500 dark:text-gray-500'}`}>
                                      {formatValue(cf.newValue, cf.field)}
                                    </td>
                                    <td className={`px-4 py-2 ${!isAccepted ? 'font-semibold text-green-700 dark:text-green-400' : 'text-gray-500 dark:text-gray-500 line-through'}`}>
                                      {formatValue(cf.oldValue, cf.field)}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 flex justify-between items-center gap-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 bg-red-200 hover:bg-red-300 dark:bg-red-700 dark:hover:bg-red-600 text-red-800 dark:text-red-200 rounded transition"
          >
            Annulla
          </button>
          <button
            onClick={handleConfirm}
            className={`px-4 py-2 bg-${themeColor}-600 hover:bg-${themeColor}-700 text-white rounded transition`}
          >
            Esegui censimento
          </button>
        </div>
      </div>
    </div>
  );
};

export default CensimentoDifferencesModal;
